import {AfterViewInit, Component, computed, OnDestroy, OnInit, signal, ViewChild} from "@angular/core";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {TranslateService} from "@ngx-translate/core";
import {FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors} from "@angular/forms";
import {MultiSelect} from "primeng/multiselect";
import {ScrollerOptions} from "primeng/api";
import {ColDef, GridApi, GridReadyEvent} from 'ag-grid-community';
import {Subscription} from 'rxjs';
import { NumericEditorComponent } from '../../common/cell-editors/numeric/numeric-editor.component';
import { ValidatorFactory } from '../../common/validator-factory';
import {NewHireParams, PlanParam} from '../../common/plan-param';
import { NumberUtils } from 'src/app/common/utils/number.utils';
import { EnterpriseCoreModule } from "@ag-grid-enterprise/core";
import { ClipboardModule } from "@ag-grid-enterprise/clipboard";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { TimeOrDecimalEditorComponent } from '../../common/cell-editors/time-or-decimal/time-or-decimal-editor.component';
import {ValidationResult, WfmTextboxComponent} from "../../../../components/wfm-textbox/wfm-textbox.component";
import {PlanHelper} from "../../../../helpers/plan-helper";
import {Actions, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {AppActionTypes, DeleteLearningCurve, DeleteLearningCurveSuccess} from "../../../../store/actions";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {map} from "rxjs/operators";
import { PlanDetailValidationCellComponent } from "../../plan-detail-validation-cell/plan-detail-validation-cell.component";
import { LearningCurve, TrainingWeekData } from "src/app/models/new-hire";
import { DayOfWeek, DEFAULT_WORKING_DAYS } from "./working-days.util";
import { WeekDaysHeaderComponent } from "./week-days-header/week-days-header.component";


const AHT = "aht";


@Component({
  standalone: false,
  selector: "wfm-esp-learning-curve-dialog",
  templateUrl: "./learning-curve-dialog.component.html",
  styleUrls: ["./learning-curve-dialog.component.css"]
})
export class LearningCurveDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  public isLoading = false;
  public enableCancel = true;

  public planName = '';
  public formSub: Subscription;
  modules = [ClipboardModule, RangeSelectionModule, EnterpriseCoreModule]
  public rowClassRules: any;
  public planId: any;
  public isEditMode = false;
  public existingLearningCurve: any = null;
  public hasClasses: boolean = false;
  public maxTrainingWeeks: number = 26; // Default fallback value
  // Plan start day of week (from new-hire settings); orders the working-days popover.
  public weekStartDay: DayOfWeek = 'MONDAY';

  readonly DEFAULT_WEEKS = 4;

  // Grid properties
  public gridApi: GridApi;
  public columnDefs: ColDef[];
  public gridData: any[];
  public weekWorkingDays: Record<string, DayOfWeek[]> = {};
  public defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true,
    editable: true,
    cellStyle: { textAlign: 'right' },
    cellDataType: false
  };

  public contactTypesInput = signal<Array<{ oid: string; id: string; name: string; label: string }>>([]);
  public contactTypes = computed(() => {
    const formattedContactTypes = this.contactTypesInput().map(ct => ({
      value: {
        ...ct,
        disabled: true
      },
      isSelected: true,
      isReadOnly: true
    }));

    setTimeout(() => {
      this.notifyContactTypesListener();
      this.learningCurveForm.markAsPristine();
    }, 0);

    return formattedContactTypes;
  });

  public selectedContactTypes: any[] = [];
  public virtualScrollOptions: ScrollerOptions = {
    autoSize: false,
    scrollHeight: '120px'
  };

  @ViewChild('ctSelector') ctSelector: MultiSelect;
  @ViewChild('nameInput') learningCurveName: WfmTextboxComponent;

  public learningCurveForm: FormGroup;

  public frameworkComponents = {
    numericEditor: NumericEditorComponent,
    timeOrDecimalEditor: TimeOrDecimalEditorComponent,
    planDetailValidationCell: PlanDetailValidationCellComponent,
    weekDaysHeader: WeekDaysHeaderComponent
  };

  public _dirtyCells = new Set<string>();
  public _invalidCells = new Set<string>();
  public _workingDaysInvalidCells = new Set<string>();
  public _isGridValid = true;
  public _weeksWithDataButNoDays: number[] = [];
  public locale = 'en-US';
  ahtValidator: any;
  productiveTimeValidator: any;
  public gridContext: any = {
    isGridValid: () => {
      return this._isGridValid;
    },
    isValidCell: (cellInfo: any) => {
      const id = this.getCellID(cellInfo);
      return !this._invalidCells.has(id) && !this._workingDaysInvalidCells.has(id);
    },
    isWorkingDaysInvalidCell: (cellInfo: any) => {
      return this._workingDaysInvalidCells.has(this.getCellID(cellInfo));
    },
    workingDaysValidationMessage: (cellInfo: any) => {
      return this._workingDaysInvalidCells.has(this.getCellID(cellInfo))
        ? this.translate.instant('learning.curve.dialog.working.days.clear.values.msg.cell')
        : null;
    },
    getWorkingDays: (weekKey: string) => this.weekWorkingDays[weekKey] ?? [],
    setWorkingDays: (weekKey: string, days: DayOfWeek[]) => this.setWorkingDays(weekKey, days),
    applyWorkingDaysToAll: (weekKey: string) => this.applyWorkingDaysToAll(weekKey),
    isTableEnabled: () => !this.disableTable,
    getWeekStartDay: () => this.weekStartDay
  };

  constructor(
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private fb: FormBuilder,
    private store: Store<any>,
    private action$: Actions
  ) {
    // Create validators once and store in class fields
    this.ahtValidator = ValidatorFactory.getValidator(
      PlanParam.AHT,
      { minValue: 0, maxValue: 99999.99 },
      this.translate,
      this.locale
    );
    this.productiveTimeValidator = ValidatorFactory.getValidator(
      NewHireParams.PRODUCTIVE_PERCENT,
      { minValue: 0, maxValue: 100 },
      this.translate,
      this.locale
    );
    // Initialize dynamic columns and data for 4 weeks by default
    this.columnDefs = this.getDynamicColumnDefs(4);
    this.gridData = this.getDynamicGridData(4);
    this.initWeekWorkingDays();

    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      editable: () => !this.disableTable,
      cellStyle: { textAlign: 'right' },
      cellRenderer: "planDetailValidationCell",
      cellDataType: false,
      onCellValueChanged: (params: any) => {
        this.cellValueChanged(params);
      }
    }

    this.rowClassRules = {
      'editable-row': (params) => !this.disableTable,
      'readonly-row': (params) => this.disableTable
    }

    this.initHandlers();
  }

  cellValueChanged(cellInfo: any) {
    this._dirtyCells.add(this.getCellID(cellInfo));
    this.updateGridValid();
    this.updateWorkingDaysValidity();
  }

  initWeekWorkingDays(existing?: TrainingWeekData[]): void {
    this.weekWorkingDays = {};
    for (let i = 1; i <= 26; i++) {
      const existingDays = existing?.[i - 1]?.workingDays;
      this.weekWorkingDays[`week${i}`] =
        existingDays !== undefined ? [...existingDays] : [...DEFAULT_WORKING_DAYS];
    }
  }

  setWorkingDays(weekKey: string, days: DayOfWeek[]): void {
    this.weekWorkingDays[weekKey] = [...days];
    this.learningCurveForm.markAsDirty();
    this.updateWorkingDaysValidity();
    this.gridApi?.refreshCells({ force: true });
  }

  applyWorkingDaysToAll(sourceWeekKey: string): void {
    const source = [...(this.weekWorkingDays[sourceWeekKey] ?? [])];
    const weeks = this.learningCurveForm.get('classTrainingWeeks').value || 0;
    for (let i = 1; i <= weeks; i++) {
      this.weekWorkingDays[`week${i}`] = [...source];
    }
    this.learningCurveForm.markAsDirty();
    this.updateWorkingDaysValidity();
    this.gridApi?.refreshHeader();
    this.gridApi?.refreshCells({ force: true });
  }

  private initializeForm(): void {
    this.learningCurveForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(32)]],
      contactTypes: [[], [Validators.required, Validators.minLength(1)]],
      classTrainingWeeks: [
        Math.min(this.DEFAULT_WEEKS,this.maxTrainingWeeks),
        [
          Validators.required,
          this.validateTrainingWeeks.bind(this)]
      ]
    });
  }

  ngOnInit(): void {
    this.initializeForm();

    // Initialize edit mode if existingLearningCurve is provided
    if (this.existingLearningCurve) {
      this.initializeEditMode();
    }
  }

  get enableAccept() {
    return this.learningCurveForm.valid && this._isGridValid && !this.hasWorkingDaysError && this.hasChanges;
  }

  private initializeEditMode(): void {
    this.isEditMode = true;
    // Set form values from existing learning curve
    this.learningCurveForm.patchValue({
      name: this.existingLearningCurve.name,
      classTrainingWeeks: this.existingLearningCurve.trainingWeeks
    });

    // Set contact types
    if (this.existingLearningCurve.cts && this.contactTypesInput().length > 0) {
      const selectedCTs = this.contactTypesInput().filter(ct =>
        this.existingLearningCurve.cts.includes(ct.oid)
      );
      this.learningCurveForm.patchValue({
        contactTypes: selectedCTs
      });
    }

    // Disable training weeks field if learning curve has classes
    if (this.hasClasses) {
      this.learningCurveForm.get('classTrainingWeeks').disable();
    }

    // Update grid with existing training week data
    if (this.existingLearningCurve.trainingWeeksData) {
      this.updateGridWithExistingData();
    }

    // Mark form as pristine after initialization
    this.learningCurveForm.markAsPristine();

    // Clear any existing dirty cells since we're loading existing data
    this._dirtyCells.clear();
    this._invalidCells.clear();
  }

  private updateGridWithExistingData(): void {
    const weeks = this.existingLearningCurve.trainingWeeks;
    this.initWeekWorkingDays(this.existingLearningCurve.trainingWeeksData);
    this.columnDefs = this.getDynamicColumnDefs(weeks);
    this.gridData = this.getDynamicGridData(weeks);

    // Clear any existing dirty cells since we're loading existing data
    this._dirtyCells.clear();
    this._invalidCells.clear();

    // Populate grid with existing data
    if (this.existingLearningCurve.trainingWeeksData) {
      this.existingLearningCurve.trainingWeeksData.forEach((weekData: TrainingWeekData, index: number) => {
        const weekKey = `week${index + 1}`;

        // Update AHT row
        const ahtRow = this.gridData.find(row =>
          row.parameter === AHT
        );
        if (ahtRow && weekData.aht !== undefined) {
          ahtRow[weekKey] = { value: weekData.aht };
        }

        // Update productive percent row
        const productiveRow = this.gridData.find(row =>
          row.parameter === NewHireParams.PRODUCTIVE_PERCENT
        );
        if (productiveRow && weekData.percentageProductive !== undefined) {
          productiveRow[weekKey] = { value: weekData.percentageProductive };
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
  }

  setName(value: string) {
    if(this.learningCurveForm.get('name').value !== value) {
      this.learningCurveForm.get('name').setValue(value);
      this.learningCurveForm.markAsDirty();
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    //this.gridApi.sizeColumnsToFit();
  }

  updateTable(): void {
    if (this.enableUpdateTable) {
      const weeks = this.learningCurveForm.get('classTrainingWeeks').value;
      // Update columnDefs and gridData dynamically
      this.columnDefs = this.getDynamicColumnDefs(weeks);
      this.gridData = this.getDynamicGridData(weeks);
      // Re-run working-days validity so stale entries for now-hidden weeks are cleared
      this.updateWorkingDaysValidity();
      // Do not resize columns to fit; allow horizontal scrolling
      // Mark the field as pristine after update
      const classTrainingWeeksControl = this.learningCurveForm.get('classTrainingWeeks');
      if (classTrainingWeeksControl) {
        classTrainingWeeksControl.markAsPristine();
      }

      this.learningCurveForm.markAsDirty();

      // Refresh row styles after marking as pristine since this changes disableTable
      if (this.gridApi) {
        this.gridApi.redrawRows();
      }
    }
  }

  submit(): void {
    if (this.enableAccept) {
      const formValue = this.learningCurveForm.getRawValue();
      const trainingWeeksData: TrainingWeekData[] = [];
      const weeks = formValue.classTrainingWeeks?formValue.classTrainingWeeks:0;

      for (let i = 1; i <= weeks; i++) {
        const weekKey = `week${i}`;
        const ahtRow = this.gridData.find(row => row.parameter === AHT);
        const percentageProductive = this.gridData.find(row => row.parameter === NewHireParams.PRODUCTIVE_PERCENT);

        trainingWeeksData.push({
          aht: ahtRow[weekKey]?.value || null,
          percentageProductive: percentageProductive[weekKey]?.value || null,
          workingDays: this.weekWorkingDays[weekKey] ?? []
        });
      }

      // Create the learning curve object using the proper interface
      const learningCurve: Partial<LearningCurve> = {
        name: formValue.name,
        trainingWeeks: weeks,
        cts: formValue.contactTypes.map(ct => ct.oid),
        trainingWeeksData: trainingWeeksData
      };

      if (this.isEditMode && this.existingLearningCurve) {
        // Add the learning curve ID for edit mode
        learningCurve.learningCurveId = this.existingLearningCurve.learningCurveId;

        this.store.dispatch({
          type: AppActionTypes.SaveLearningCurve,
          payload: { planId: this.planId, ...learningCurve }
        });
      } else {
        this.store.dispatch({
          type: AppActionTypes.SaveLearningCurve,
          payload: { planId: this.planId, ...learningCurve }
        });
      }

      this.isLoading = true;
      this.enableCancel = false;
    }
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  validateTrainingWeeks(control: AbstractControl): ValidationErrors | null {
    const weeks = control.value;

    // Check if weeks has a value (not null, undefined, or empty string)
    if (weeks === null || weeks === undefined || weeks === '') {
      return { required: true };
    }

    // Check if weeks is negative
    if (weeks < 0) {
      return { invalidWeeks: true };
    }

    // Check if weeks exceeds the maximum allowed
    if (weeks > this.maxTrainingWeeks) {
      return { max: this.maxTrainingWeeks, actual: weeks };
    }

    return null; // Validation passes
  }

  get enableDelete() {
    return this.isEditMode && !this.isLoading;
  }

  get isFormValid() {
    // come back and add the contact types validation
    const trainingWeeksValid = this.disableTrainingWeeksField || this.learningCurveForm.get('classTrainingWeeks').valid;
    return this.hasValidLearningCurveName() && trainingWeeksValid && this._isGridValid && !this.hasWorkingDaysError;
  }

  get hasChanges() {
    // Check if form has been modified
    const formDirty = this.learningCurveForm.dirty;

    // Check if grid has been modified (has dirty cells)
    const gridDirty = this._dirtyCells.size > 0;

    return formDirty || gridDirty;
  }

  get enableUpdateTable() {
    return this.isTrainingWeeksValid() && !this.disableTrainingWeeksField;
  }

  isTrainingWeeksValid(): boolean {
    return this.learningCurveForm.get('classTrainingWeeks').valid && this.learningCurveForm.get('classTrainingWeeks').dirty;
  }

  get disableTable() {
    return this.learningCurveForm.get('classTrainingWeeks').invalid || this.learningCurveForm.get('classTrainingWeeks').dirty;
  }

  get disableTrainingWeeksField() {
    // Disable training weeks field when editing a learning curve that has associated classes
    // This prevents breaking existing class data by changing the training weeks
    return this.isEditMode && this.hasClasses;
  }

  getCellID(params: any): string {
    return params.data.parameter + '|' + params.colDef.field;
  }

  updateGridValid(): void {
    let result = true;
    this._dirtyCells.forEach((cellId: string) => {
      const [parameter, week] = cellId.split('|');
      const row = this.gridData.find((r) => r.parameter === parameter);
      if (row) {
        const value = row[week]?.value;
        const validator = parameter === 'aht' ? this.ahtValidator : this.productiveTimeValidator;
        const isValid = validator ? validator.isValid(value) : true;
        if (!isValid) {
          this._invalidCells.add(cellId);
        } else {
          this._invalidCells.delete(cellId);
        }
        result = result && isValid;
      }
    });
    this._isGridValid = result;
  }

  updateWorkingDaysValidity(): void {
    const weeks = this.learningCurveForm.get('classTrainingWeeks').value || 0;
    const ahtRow = this.gridData.find((r) => r.parameter === AHT);
    const productiveRow = this.gridData.find((r) => r.parameter === NewHireParams.PRODUCTIVE_PERCENT);
    const invalid: number[] = [];
    this._workingDaysInvalidCells.clear();

    for (let i = 1; i <= weeks; i++) {
      const weekKey = `week${i}`;
      const days = this.weekWorkingDays[weekKey] ?? [];
      if (days.length > 0) {
        continue;
      }
      const ahtValue = ahtRow?.[weekKey]?.value;
      const productiveValue = productiveRow?.[weekKey]?.value;
      // A 0 (or empty/blank) value is treated as "no value" — a 0-day week
      // holding only zeros/blanks is valid.
      const ahtHasValue = this.hasNonZeroValue(ahtValue);
      const productiveHasValue = this.hasNonZeroValue(productiveValue);
      if (ahtHasValue) {
        this._workingDaysInvalidCells.add(`${AHT}|${weekKey}`);
      }
      if (productiveHasValue) {
        this._workingDaysInvalidCells.add(`${NewHireParams.PRODUCTIVE_PERCENT}|${weekKey}`);
      }
      if (ahtHasValue || productiveHasValue) {
        invalid.push(i);
      }
    }
    this._weeksWithDataButNoDays = invalid;
  }

  private hasNonZeroValue(value: any): boolean {
    if (value === '' || value === null || value === undefined) {
      return false;
    }
    return Number(value) !== 0;
  }

  get hasWorkingDaysError(): boolean {
    return this._weeksWithDataButNoDays.length > 0;
  }

  floor(num: any, dec: any): string {
    if (num == null) {
      return '';
    }

    if (num?.toString().includes('.')) {
      let array = num.toString().split('.');
      let decPart = array.pop();
      if (!decPart) {
        decPart = '0';
      }
      array.push(decPart.substring(0, dec));
      let result = array.join('.');
      return result;
    } else {
      return num.toString();
    }
  }

  setProductiveTimeValue(params: any): boolean {
    let newValRaw = params.newValue;
    let oldValRaw = params.oldValue;
    //let newVal = NumberUtils.decimalOrTimeToTime(newValRaw);
    //let oldVal = NumberUtils.decimalOrTimeToTime(oldValRaw);
    if (newValRaw !== oldValRaw) {
      params.data[params.colDef.field] = { value: newValRaw };
      this._dirtyCells.add(this.getCellID(params));
      this.updateGridValid();
      this.updateWorkingDaysValidity();
      return true;
    }
    return false;
  }

  setNumericValue(params: any): boolean {
    const decimal = Number(params.data.metadata.decimal);
    let newValRaw = params.newValue;
    let oldValRaw = params.oldValue;
    let newVal = newValRaw === '' ? NaN : parseFloat(this.floor(newValRaw, decimal));
    let oldVal = oldValRaw === '' ? NaN : parseFloat(this.floor(oldValRaw, decimal));
    if (newVal !== oldVal) {
      params.data[params.colDef.field] = { value: isNaN(newVal) ? '' : newVal };
      this._dirtyCells.add(this.getCellID(params));
      this.updateGridValid();
      this.updateWorkingDaysValidity();
      return true;
    }
    return false;
  }

  // Helper to generate week columns dynamically
  // private generateWeekColumns(weeks: number) {
  //   // Always generate up to 26 weeks for data preallocation
  //   return Array.from({ length: 26 }, (_, i) => `week${i + 1}`);
  // }

  // Helper to generate column definitions dynamically
  private getDynamicColumnDefs(weeks: number): ColDef[] {
    // Only show columns up to the selected number of weeks
    const weekColumns = Array.from({ length: weeks }, (_, i) => `week${i + 1}`).map((week) => ({
      field: week,
      headerName: week.replace('week', 'Week '),
      width: 100,
      headerComponent: 'weekDaysHeader',
      resizable: false,
      cellEditorSelector: (params) => {
        // Always use numericEditor for both parameters
        return { component: 'numericEditor' };
      },
      cellEditorParams: (params) => {
        return {
          origValue: params.data[params.colDef.field]?.value ?? '',
          metadata: params.data.metadata,
          zeroOnEmpty: false,
          locale: this.locale,
          allowNaN: true,
          context: {
            isLoading: () => {
              return this.isLoading;
            }
          }
        };
      },
      valueGetter: (params) => {
        const val = params.data[params.colDef.field];
        return val && val.value !== undefined ? val.value : '';
      },
      valueSetter: (params) => {
        // Always use setNumericValue for both parameters
        return this.setNumericValue(params);
      },
      valueFormatter: (params) => {
        if (params?.data?.metadata) {
          let options = { decimal: params.data.metadata.decimal };
          return NumberUtils.formatNumberIfNecessary(params.value, this.locale, options.decimal);
        }
        return params.value;
      },
      editable: () => !this.disableTable,
      cellClassRules: {
        'invalid': (params) => !this.gridContext.isValidCell(params),
      }
    }));
    return [
      {
        field: 'parameter',
        headerName: 'Parameter',
        pinned: 'left',
        width: 200,
        cellStyle: { textAlign: 'left' },
        editable: false,
        valueGetter: (params) => {
          return params.data.parameter === AHT ? this.translate.instant("plan.detail.aht.seconds") : this.translate.instant("plan.detail.productive.percent");
        }
      },
      ...weekColumns
    ];
  }

  // Helper to generate grid data dynamically, preserving values if possible
  private getDynamicGridData(weeks: number): any[] {
    // Always preallocate all 26 weeks in the data
    const weekFields = Array.from({length: 26}, (_, i) => `week${i + 1}`);
    const hasOldData = Array.isArray(this.gridData) && this.gridData.length > 0;
    const parameters = [
      {
        parameter: AHT,
        parameter_name: this.translate.instant("plan.detail.aht.seconds"),
        metadata: {
          decimal: 2,
          validator: {
            isValid: (value: any) => {
              return true;
            },
            msg: this.translate.instant("validation.err.actual_aht")
          }
        }
      },
      {
        parameter: NewHireParams.PRODUCTIVE_PERCENT,
        parameter_name: this.translate.instant("plan.detail.productive.percent"),
        metadata: {
          decimal: 2,
          validator: {
            isValid: (value: any) => {
              return true;
            },
            msg: this.translate.instant("validation.err.productive_percent")
          }
        }
      }
    ];
    return parameters.map((param, idx) => {
      const oldRow = hasOldData ? (this.gridData[idx] || {}) : {};
      const row: any = {parameter: param.parameter, metadata: param.metadata};
      weekFields.forEach((week) => {
        row[week] = hasOldData && oldRow[week] !== undefined ? oldRow[week] : {value: ''};
      });
      return row;
    });
  }

  ngAfterViewInit(): void {
    this.learningCurveName.validators.push(this.nameValidator.bind(this));
  }

  nameValidator(name: string) {
    if (name.length < 1) {
      return new ValidationResult(this.translate.instant("field.required.msg"));
    } else if (PlanHelper.planNameRegExStartOrEndWithSpecialCharacter.test(name)) {
      return new ValidationResult(this.translate.instant("learning.curve.name.error.special.character.start.end"));
    } else if (!PlanHelper.planNameRegEx.test(name)) {
      return new ValidationResult(this.translate.instant("learning.curve.name.error.disallowed.characters"));
    } else {
      return new ValidationResult("");
    }
  }

  hasValidLearningCurveName() {
    const nameValue = this.learningCurveForm.get('name')?.value?.trim() || '';
    return !this.learningCurveName?.hasClientError && nameValue.length > 0;
  }

  notifyContactTypesListener() {
    if (this.ctSelector) {
      this.ctSelector.selectedOptions = this.ctSelector._options().map(opt => opt.value);
      this.ctSelector.updateModel(this.ctSelector.selectedOptions);
      this.ctSelector.onModelChange(this.ctSelector.value);
      this.ctSelector.onChange.emit({originalEvent: null, value: this.ctSelector.value});
    }
  }

  initHandlers() {
    this.action$.pipe(
      ofType(AppActionTypes.SaveLearningCurveSuccess),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.saveLearningCurveSuccessHandler.bind(this));

    this.action$.pipe(
      ofType(AppActionTypes.SaveLearningCurveFailed),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.saveLearningCurveFailureHandler.bind(this));

    this.action$.pipe(
      ofType(AppActionTypes.DeleteLearningCurveSuccess),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.deleteLearningCurveSuccessHandler.bind(this));

    this.action$.pipe(
      ofType(AppActionTypes.DeleteLearningCurveFailed),
      map((action: any) => action.payload),
      takeUntilDestroyed()
    ).subscribe(this.deleteLearningCurveFailedHandler.bind(this));
  }

  saveLearningCurveSuccessHandler(data: any) {
    let learningCurve: LearningCurve = {
      learningCurveId: data.learningCurveId,
      name: data.name,
      trainingWeeks: data.trainingWeeks,
      cts: JSON.parse(JSON.stringify(data.cts)),
      trainingWeeksData: JSON.parse(JSON.stringify(data.trainingWeeksData)),
      classes: data.classes || []
    };

    this.isLoading = false;
    this.enableCancel = true;

    // Close the dialog and return the data
    this.activeModal.close(learningCurve);
  }

  saveLearningCurveFailureHandler(error: any) {
    this.isLoading = false;
    this.enableCancel = true;
  }

  submitDelete(): void {
    if (this.isEditMode && this.existingLearningCurve) {
      this.enableCancel = false;
      this.isLoading = true;
      this.store.dispatch(new DeleteLearningCurve({
        planId: this.planId,
        learningCurveId: this.existingLearningCurve.learningCurveId
      }));
    } else {
      this.activeModal.close();
    }
  }

  deleteLearningCurveSuccessHandler(data: any) {
    this.isLoading = false;
    this.enableCancel = true;
    this.activeModal.close({ delete: true });
  }

  deleteLearningCurveFailedHandler(error: any) {
    this.isLoading = false;
    this.enableCancel = true;
  }
}
